import type { AdhocCallSummary } from "../../api/client";

/**
 * Search, sort and pagination for the call results data table
 * (CallResultsPanel.tsx). Pure functions over rows the API already
 * returned — everything happens client-side on GET /v1/adhoc-calls' own
 * list, with no new query parameters on the server.
 */
export type SortKey = "to" | "from" | "trunk" | "placed" | "duration" | "result";
export type SortDir = "asc" | "desc";

/** Answered-to-ended time in seconds, or null when the call never connected. */
export function durationSeconds(row: AdhocCallSummary): number | null {
  if (!row.answered_at || !row.ended_at) return null;
  return Math.max(0, (new Date(row.ended_at).getTime() - new Date(row.answered_at).getTime()) / 1000);
}

/**
 * Case-insensitive substring match across every value a person might search
 * by: the numbers and trunk shown in the table, the result label, and the
 * ids/causes only visible in a row's expanded detail — including a
 * /v1/send-msg request's own uid and type.
 */
export function matchesQuery(row: AdhocCallSummary, resultLabel: string, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (!needle) return true;
  const haystack = [
    row.destination,
    row.caller_id,
    row.trunk_ref,
    resultLabel,
    row.adhoc_call_id,
    row.call_id,
    row.uid,
    row.type,
    row.status,
    row.terminal_reason,
    row.amd_result,
    row.hangup_cause,
  ];
  return haystack.some((value) => value?.toLowerCase().includes(needle));
}

function compareKey(
  a: AdhocCallSummary,
  b: AdhocCallSummary,
  key: SortKey,
  resultLabel: (row: AdhocCallSummary) => string,
): number {
  switch (key) {
    case "to":
      return a.destination.localeCompare(b.destination);
    case "from":
      return (a.caller_id ?? "").localeCompare(b.caller_id ?? "");
    case "trunk":
      return a.trunk_ref.localeCompare(b.trunk_ref);
    case "placed":
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    case "duration":
      // sortRows has already put never-connected rows last; two of them tie.
      return (durationSeconds(a) ?? 0) - (durationSeconds(b) ?? 0);
    case "result":
      return resultLabel(a).localeCompare(resultLabel(b));
  }
}

/** A stable sort: rows that compare equal keep the API's own order. */
export function sortRows(
  rows: AdhocCallSummary[],
  key: SortKey,
  dir: SortDir,
  resultLabel: (row: AdhocCallSummary) => string,
): AdhocCallSummary[] {
  const sign = dir === "asc" ? 1 : -1;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((x, y) => {
      if (key === "duration") {
        // Keep never-connected rows last in BOTH directions.
        const nx = durationSeconds(x.row) === null;
        const ny = durationSeconds(y.row) === null;
        if (nx !== ny) return nx ? 1 : -1;
      }
      return sign * compareKey(x.row, y.row, key, resultLabel) || x.index - y.index;
    })
    .map(({ row }) => row);
}

export interface Page<T> {
  rows: T[];
  /** 1-based, clamped into range. */
  page: number;
  pageCount: number;
  /** 1-based index of the first row shown, 0 when there are none. */
  first: number;
  /** 1-based index of the last row shown. */
  last: number;
  total: number;
}

export function paginate<T>(rows: T[], page: number, pageSize: number): Page<T> {
  const total = rows.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.min(Math.max(1, page), pageCount);
  const start = (current - 1) * pageSize;
  const slice = rows.slice(start, start + pageSize);
  return {
    rows: slice,
    page: current,
    pageCount,
    first: slice.length ? start + 1 : 0,
    last: start + slice.length,
    total,
  };
}

/**
 * The page numbers to show as buttons: the first, the last, and two either
 * side of the current page, with null marking a gap ("…") between them.
 */
export function pageNumbers(page: number, pageCount: number): (number | null)[] {
  const shown = new Set([1, pageCount]);
  for (let n = page - 2; n <= page + 2; n++) {
    if (n >= 1 && n <= pageCount) shown.add(n);
  }
  const sorted = [...shown].sort((a, b) => a - b);
  const out: (number | null)[] = [];
  let previous: number | undefined;
  for (const n of sorted) {
    if (previous !== undefined && n - previous > 1) out.push(null);
    out.push(n);
    previous = n;
  }
  return out;
}
